import { useCallback, useEffect, useRef, useState } from 'react'
import loadable from '@loadable/component'
import { registerCaptchaHandler, unregisterCaptchaHandler } from 'lib/waf-bridge'

//
// Coordination for the AWS WAF CAPTCHA, kept deliberately tiny and eager so the
// main bundle carries only the bridge subscription. The puzzle itself is a rare
// path and pulls in Modal/focus-trap-react, which nothing else in the main entry
// needs — so the presentation is code-split the same way App.js splits every
// other modal.
//
// Rendered conditionally rather than always-mounted, since @loadable/component
// fetches its chunk on mount: an always-mounted lazy component would download on
// every page load and defeat the split.
//
const WafCaptchaModal = loadable(() => import('components/modals/WafCaptchaModal'))

type Pending = { resolve: (token: string) => void; reject: (err: Error) => void }

const WafGate = () => {
  const [challenging, setChallenging] = useState(false)
  //
  // A queue, not a single slot. WAF challenges every request in flight, so a
  // page load that fires N parallel calls produces N requestCaptchaSolve()
  // calls. Holding one pending would drop all but the last — leaving those
  // axios promises unsettled forever (a permanently hung request, not an
  // error). One puzzle is shown and its token fans out to every waiter.
  //
  const pendingRef = useRef<Pending[]>([])

  const settle = useCallback((outcome: { token: string } | { error: Error }) => {
    setChallenging(false)
    const waiting = pendingRef.current
    pendingRef.current = []
    waiting.forEach(pending => {
      if ('token' in outcome) {
        pending.resolve(outcome.token)
      } else {
        pending.reject(outcome.error)
      }
    })
  }, [])

  useEffect(() => {
    registerCaptchaHandler(
      () =>
        new Promise<string>((resolve, reject) => {
          pendingRef.current.push({ resolve, reject })
          // Idempotent: a challenge arriving while the puzzle is already open
          // joins the queue rather than opening a second one.
          setChallenging(true)
        })
    )

    return () => {
      unregisterCaptchaHandler()
      // Unmounting mid-challenge must not leave requests hanging.
      const waiting = pendingRef.current
      pendingRef.current = []
      waiting.forEach(pending => pending.reject(new Error('CAPTCHA cancelled')))
    }
  }, [])

  const onSolve = useCallback((token: string) => settle({ token }), [settle])
  const onCancel = useCallback((error: Error) => settle({ error }), [settle])

  if (!challenging) {
    return null
  }

  return <WafCaptchaModal onSolve={onSolve} onCancel={onCancel} />
}

export default WafGate
